import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import { QueryKeys } from '../enums/QueryKeys';
import { useQuery } from '@tanstack/react-query';
import {
  QueryOptions,
  QueryResult,
  TrashbagsBatchDto,
  TrashbagsBatchByIdDto,
  TrashbagsAccountNumbersDto,
  TrashbagsCollectionsDto,
  TruckCollectionAggregationDto,
  TruckCollectionDto,
  ContainerCollectionDto,
  TrucksDto,
  ContainersDto,
} from '@thingslog/repositories';

export interface TrashbagsCollectionsQueryParams {
  fromDate: string;
  toDate: string;
  accountId?: number;
}

export interface TruckCollectionAggregationQueryParams {
  fromDate: string;
  toDate: string;
  truckNumber?: string;
}

export interface TruckCollectionQueryParams {
  fromDate: string;
  toDate: string;
  truckNumber?: string;
}

export interface ContainerCollectionQueryParams {
  fromDate: string;
  toDate: string;
  containerNumber?: string;
}

export interface TrashbagsQueryClient {
  useGetTrashbagsBatch: (
    options?: QueryOptions<TrashbagsBatchDto>
  ) => QueryResult<TrashbagsBatchDto>;
  getTrashbagsBatch: () => Promise<TrashbagsBatchDto>;
  useGetTrashbagsBatchById: (
    id: number,
    options?: QueryOptions<TrashbagsBatchByIdDto>
  ) => QueryResult<TrashbagsBatchByIdDto>;
  getTrashbagsBatchById: (id: number) => Promise<TrashbagsBatchByIdDto>;
  useGetTrashbagsAccountNumbers: (
    options?: QueryOptions<TrashbagsAccountNumbersDto>
  ) => QueryResult<TrashbagsAccountNumbersDto>;
  getTrashbagsAccountNumbers: () => Promise<TrashbagsAccountNumbersDto>;
  useGetTrashbagsCollections: (
    params: TrashbagsCollectionsQueryParams,
    options?: QueryOptions<TrashbagsCollectionsDto>
  ) => QueryResult<TrashbagsCollectionsDto>;
  getTrashbagsCollections: (
    params: TrashbagsCollectionsQueryParams
  ) => Promise<TrashbagsCollectionsDto>;
  useGetTruckCollectionAggregation: (
    params: TruckCollectionAggregationQueryParams,
    options?: QueryOptions<TruckCollectionAggregationDto>
  ) => QueryResult<TruckCollectionAggregationDto>;
  getTruckCollectionAggregation: (
    params: TruckCollectionAggregationQueryParams
  ) => Promise<TruckCollectionAggregationDto>;
  useGetTruckCollection: (
    params: TruckCollectionQueryParams,
    options?: QueryOptions<TruckCollectionDto>
  ) => QueryResult<TruckCollectionDto>;
  getTruckCollection: (params: TruckCollectionQueryParams) => Promise<TruckCollectionDto>;
  useGetContainerCollection: (
    params: ContainerCollectionQueryParams,
    options?: QueryOptions<ContainerCollectionDto>
  ) => QueryResult<ContainerCollectionDto>;
  getContainerCollection: (
    params: ContainerCollectionQueryParams
  ) => Promise<ContainerCollectionDto>;
  useGetTrucks: (options?: QueryOptions<TrucksDto>) => QueryResult<TrucksDto>;
  getTrucks: () => Promise<TrucksDto>;
  useGetContainers: (options?: QueryOptions<ContainersDto>) => QueryResult<ContainersDto>;
  getContainers: () => Promise<ContainersDto>;
}

export function createTrashbagsQueryClient(axios: AxiosInstance): TrashbagsQueryClient {
  return new TrashbagsQueryImp(axios);
}

class TrashbagsQueryImp {
  public constructor(private axios: AxiosInstance) {}

  public useGetTrashbagsBatch = (
    options?: QueryOptions<TrashbagsBatchDto>
  ): QueryResult<TrashbagsBatchDto> => {
    return useQuery<TrashbagsBatchDto, AxiosError>(
      [QueryKeys.GetTrashbagsBatch],
      () => this.getTrashbagsBatch(),
      options
    );
  };

  public getTrashbagsBatch = async (): Promise<TrashbagsBatchDto> => {
    return await this.axios
      .get(`/api/trashbags/batch`)
      .then((response: AxiosResponse<TrashbagsBatchDto>) => {
        return response.data;
      });
  };

  public useGetTrashbagsBatchById = (
    id: number,
    options?: QueryOptions<TrashbagsBatchByIdDto>
  ): QueryResult<TrashbagsBatchByIdDto> => {
    return useQuery<TrashbagsBatchByIdDto, AxiosError>(
      [QueryKeys.GetTrashbagsBatchById, id],
      () => this.getTrashbagsBatchById(id),
      options
    );
  };

  public getTrashbagsBatchById = async (id: number): Promise<TrashbagsBatchByIdDto> => {
    return await this.axios
      .get(`/api/trashbags/batch/${id}`)
      .then((response: AxiosResponse<TrashbagsBatchByIdDto>) => {
        return response.data;
      });
  };

  public useGetTrashbagsAccountNumbers = (
    options?: QueryOptions<TrashbagsAccountNumbersDto>
  ): QueryResult<TrashbagsAccountNumbersDto> => {
    return useQuery<TrashbagsAccountNumbersDto, AxiosError>(
      [QueryKeys.GetTrashbagsAccountNumbers],
      () => this.getTrashbagsAccountNumbers(),
      options
    );
  };

  public getTrashbagsAccountNumbers = async (): Promise<TrashbagsAccountNumbersDto> => {
    return await this.axios
      .get(`/api/trashbags/accountNumbers`)
      .then((response: AxiosResponse<TrashbagsAccountNumbersDto>) => {
        return response.data;
      });
  };

  public useGetTrashbagsCollections = (
    params: TrashbagsCollectionsQueryParams,
    options?: QueryOptions<TrashbagsCollectionsDto>
  ): QueryResult<TrashbagsCollectionsDto> => {
    return useQuery<TrashbagsCollectionsDto, AxiosError>(
      [QueryKeys.GetTrashbagsCollections, params.fromDate, params.toDate, params.accountId],
      () => this.getTrashbagsCollections(params),
      options
    );
  };

  public getTrashbagsCollections = async (
    params: TrashbagsCollectionsQueryParams
  ): Promise<TrashbagsCollectionsDto> => {
    return await this.axios
      .get(`/api/trashbags/collections`, {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          accountId: params.accountId,
        },
      })
      .then((response: AxiosResponse<TrashbagsCollectionsDto>) => {
        return response.data;
      });
  };

  public useGetTruckCollectionAggregation = (
    params: TruckCollectionAggregationQueryParams,
    options?: QueryOptions<TruckCollectionAggregationDto>
  ): QueryResult<TruckCollectionAggregationDto> => {
    return useQuery<TruckCollectionAggregationDto, AxiosError>(
      [QueryKeys.GetTruckCollectionAggregation, params.fromDate, params.toDate, params.truckNumber],
      () => this.getTruckCollectionAggregation(params),
      options
    );
  };

  public getTruckCollectionAggregation = async (
    params: TruckCollectionAggregationQueryParams
  ): Promise<TruckCollectionAggregationDto> => {
    return await this.axios
      .get(`/api/trashbags/truck-collection/aggregation`, {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          truckNumber: params.truckNumber,
        },
      })
      .then((response: AxiosResponse<TruckCollectionAggregationDto>) => {
        return response.data;
      });
  };

  public useGetTruckCollection = (
    params: TruckCollectionQueryParams,
    options?: QueryOptions<TruckCollectionDto>
  ): QueryResult<TruckCollectionDto> => {
    return useQuery<TruckCollectionDto, AxiosError>(
      [QueryKeys.GetTruckCollection, params.fromDate, params.toDate, params.truckNumber],
      () => this.getTruckCollection(params),
      options
    );
  };

  public getTruckCollection = async (
    params: TruckCollectionQueryParams
  ): Promise<TruckCollectionDto> => {
    return await this.axios
      .get(`/api/trashbags/truck-collection`, {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          truckNumber: params.truckNumber,
        },
      })
      .then((response: AxiosResponse<TruckCollectionDto>) => {
        return response.data;
      });
  };

  public useGetContainerCollection = (
    params: ContainerCollectionQueryParams,
    options?: QueryOptions<ContainerCollectionDto>
  ): QueryResult<ContainerCollectionDto> => {
    return useQuery<ContainerCollectionDto, AxiosError>(
      [QueryKeys.GetContainerCollection, params.fromDate, params.toDate, params.containerNumber],
      () => this.getContainerCollection(params),
      options
    );
  };

  public getContainerCollection = async (
    params: ContainerCollectionQueryParams
  ): Promise<ContainerCollectionDto> => {
    return await this.axios
      .get(`/api/trashbags/container-collection`, {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          containerNumber: params.containerNumber,
        },
      })
      .then((response: AxiosResponse<ContainerCollectionDto>) => {
        return response.data;
      });
  };

  public useGetTrucks = (options?: QueryOptions<TrucksDto>): QueryResult<TrucksDto> => {
    return useQuery<TrucksDto, AxiosError>([QueryKeys.GetTrucks], () => this.getTrucks(), options);
  };

  public getTrucks = async (): Promise<TrucksDto> => {
    return await this.axios
      .get(`/api/trashbags/trucks`)
      .then((response: AxiosResponse<TrucksDto>) => {
        return response.data;
      });
  };

  public useGetContainers = (options?: QueryOptions<ContainersDto>): QueryResult<ContainersDto> => {
    return useQuery<ContainersDto, AxiosError>(
      [QueryKeys.GetContainers],
      () => this.getContainers(),
      options
    );
  };

  public getContainers = async (): Promise<ContainersDto> => {
    return await this.axios
      .get(`/api/trashbags/containers`)
      .then((response: AxiosResponse<ContainersDto>) => {
        return response.data;
      });
  };
}
